/* =========================================================================
 * THE SEARCH — every word, anywhere, in any order, and it NEVER just says no.
 *
 * Brian: "i just checked baseball little league - there must be something
 * fucking wrong. it doesnt find shit."
 * Brian: "so if it doesnt say certain words we dont get an answer - our system
 * must be stronger than this. it should be a fucking agent that is built for
 * this."
 *
 * ⚠️ THE ORIGINAL DEFECT.
 *
 * The box did ONE literal substring test against a fixed concatenation:
 *
 *     `${name} ${owner} ${sport} ${conference} ${city} …`.includes(q)
 *
 * so the query had to appear as a contiguous phrase in the order those fields
 * happen to be glued together. Measured on the real names:
 *
 *     "baseball little league"   → 0 hits
 *     "little league baseball"   → 1 hit
 *
 * Same board, same intent, different answer depending on word order.
 *
 * ⚠️ AND THE REAL LESSON, WHICH IS BIGGER THAN THE BUG.
 *
 * A search that returns a bare "0 shown" is not merely unhelpful, it is
 * MISINFORMATION: it let Brian conclude the championship universe was missing
 * Little League. The universe was fine. The query was. So the invariant this
 * module exists to hold is:
 *
 *     NEVER RETURN A SILENT ZERO. If nothing matches everything, say which
 *     word killed it and show what came closest.
 *
 * That distinction — "we hold nothing called that" vs "you typed it
 * differently to how we store it" — is the whole difference between a rep
 * trusting the board and a rep concluding it is broken.
 *
 * ⚠️ WHY THIS IS NOT AN LLM, THOUGH IT IS ASKED TO BEHAVE LIKE ONE.
 *
 * "It should be an agent built for this" is the right instinct and the wrong
 * mechanism HERE, for one hard reason: a model call on a search box runs on
 * every keystroke, against 13,341 records, per rep, forever — metered. SR's
 * standing rule is that nothing routes through a paid API. So this is built as
 * a real retrieval engine instead: domain vocabulary, typo tolerance, ranked
 * partial matching, and an honest account of what it did. Deterministic, free,
 * instant, and testable — verify-search.mjs drives these exact functions.
 *
 * The one thing a model would add that this cannot is understanding a phrase
 * nobody has ever written down. That is what ALIASES is for, and adding to it
 * is a one-line edit — which is also why it is a plain table and not a
 * cleverness.
 * ========================================================================= */

import { STATES, PROVINCES } from '../../lib/usStates.js'

const S = (v) => (v == null ? '' : String(v)).trim()

/* ---- GEOGRAPHY ----------------------------------------------------------
 * ⚠️ "NORTH CAROLINA" RETURNED NOTHING, AND SAID IT WAS A DATA GAP.
 *
 * Records store the state as a USPS CODE (`normalizeState` → NC, CA, PA), so a
 * rep typing the state's actual NAME matched nothing — and the honest-zero note
 * then reported *"nothing on the board says california — that is a gap in the
 * data"*, which is flatly untrue. Thousands of those records exist. That is the
 * silent-zero lie wearing a different hat: worse than unhelpful, because it is
 * a confident false claim about the data.
 *
 * The name↔code table already exists in lib/usStates.js — the same one the map
 * and the normalizer use — so this reuses it rather than curating a second list
 * that could drift. Canadian provinces come along for free.
 * ------------------------------------------------------------------------ */
const GEO = (() => {
  const out = new Map()
  for (const { code, name } of [...STATES, ...PROVINCES]) {
    if (!code || !name) continue
    out.set(name.toLowerCase(), code.toLowerCase())
  }
  return out
})()
/* Longest first, so "west virginia" is taken before "virginia". */
const GEO_NAMES = [...GEO.keys()].sort((a, b) => b.length - a.length)
/* The 2-letter codes themselves, for the whole-word rule below. */
const GEO_CODES = new Set([...GEO.values()])

/* ---- THE DOMAIN VOCABULARY ---------------------------------------------
 * What SR's people call things, mapped to what the records call them. This is
 * the part a general-purpose search cannot have and the part that makes this
 * feel like it knows the business.
 *
 * ⚠️ CURATED, NEVER INFERRED. Every entry is a phrase somebody at SR actually
 * uses. A guessed synonym silently widens a search and puts a wrestling record
 * in front of somebody hunting football — the failure is invisible, because a
 * search that returns too much looks like it is working.
 *
 * Both directions matter: a rep may type the short form or the long one.
 * ---------------------------------------------------------------------- */
export const ALIASES = {
  llws: ['little league world series'],
  ll: ['little league'],
  'little league': ['llws'],
  youth: ['little league', 'junior', 'jr'],
  hs: ['high school'],
  'high school': ['hs'],
  jr: ['junior'],
  sr: ['senior'],
  natl: ['national'],
  nat: ['national'],
  champs: ['championship'],
  champ: ['championship'],
  chip: ['championship'],
  title: ['championship'],
  ncaa: ['college', 'collegiate'],
  college: ['ncaa', 'collegiate'],
  d1: ['division i', 'division 1'],
  juco: ['junior college'],
  cheer: ['cheerleading'],
  cheerleading: ['cheer'],
  bball: ['basketball'],
  hoops: ['basketball'],
  fb: ['football'],
  vball: ['volleyball'],
  wrestling: ['wrestle'],
  xc: ['cross country'],
  'track and field': ['track'],
  soccer: ['football club'],
  ws: ['world series'],
  'world series': ['ws'],
  /* GENDER, as a rep says it. Records carry Boys / Girls / Men / Women / Open,
   * and a rep types whichever half of the pair they think in. */
  girls: ['women'],
  women: ['girls'],
  womens: ['women', 'girls'],
  ladies: ['women', 'girls'],
  boys: ['men'],
  men: ['boys'],
  mens: ['men', 'boys'],
  coed: ['open'],
}

/* ---- THE HAYSTACK ------------------------------------------------------ */

/**
 * ownerOf is passed IN rather than imported, because resolving an owner is
 * `ownerName()`'s job in lib/championships.js and there must be exactly one of
 * those (blank / "Unassigned" / "unknown" / an owner OBJECT all mean nobody).
 * Re-deriving it here is how a search starts disagreeing with the column it is
 * searching.
 */
export function haystack(c, ownerOf = () => '') {
  return [
    c?.id,
    c?.name,
    c?.schoolName,
    ...(c?.schools || []),
    ownerOf(c),
    c?.sport,
    c?.conference,
    c?.city,
    c?.state,
    c?.level,
    c?.division,
    /* ⚠️ EVERY COLUMN ON SCREEN MUST BE SEARCHABLE.
     *
     * `segment` is the DIVISION column a rep reads on the board — "HS State",
     * "Youth/Travel Sports", "Band/Marching Arts" — and it was not in here at
     * all, so the one word they can see was the one word that found nothing.
     * Gender and season year were missing for the same reason: nobody checked
     * the rendered columns against this list. */
    c?.segment,
    c?.gender,
    c?.seasonYear,
    /* ⚠️ THE CHAMPION. Missing until now, on a board whose entire job is
     * finding champions — a rep who remembered the winner but not the
     * competition had no way back to the record. Both spellings, because a
     * pursuit-adapted row carries one and a championship the other. */
    c?.champion,
    c?.winningSchool,
    c?.incumbent,
    /* The pursuit spaces' own identifying fields, unchanged from the original
     * list: an RFP is found by what it is for and who issued it, a corporation
     * by what it does and where it is, a department by its Community Hero. */
    c?.product,
    c?.issuer,
    c?.industry,
    c?.hqLocation,
    c?.hero?.name,
    c?.orgType,
  ].map(S).filter(Boolean).join(' ').toLowerCase()
}

/* ⚠️ THE HAYSTACK IS CACHED PER RECORD, AND IT HAS TO BE.
 *
 * This runs inside a useMemo on EVERY KEYSTROKE against the whole loaded
 * universe. Measured on 13,341 records before caching: 217ms for a three-word
 * query, 65ms for one word — synchronous, on the UI thread, per character.
 * That is a box that feels broken while you type in it, which is its own way of
 * making the board look unreliable.
 *
 * The record objects are stable between keystrokes (the query changes, the data
 * does not), so the string and its word list are built once. A WeakMap means a
 * reload's replaced records are collected rather than leaked.
 *
 * Keyed on the record OBJECT, not its id: a patchLocal that replaces a record
 * with a new object must get a fresh haystack, or an optimistic crown would
 * stay unsearchable until the next full load. */
const HAY = new WeakMap()

export function haystackOf(c, ownerOf) {
  if (c && typeof c === 'object') {
    const hit = HAY.get(c)
    if (hit) return hit
  }
  const hay = haystack(c, ownerOf)
  const entry = { hay, words: hay.split(/[^a-z0-9]+/).filter(Boolean) }
  if (c && typeof c === 'object') HAY.set(c, entry)
  return entry
}

/* ---- TYPO TOLERANCE ---------------------------------------------------- */

/**
 * Edit distance, bounded and early-exiting — we never need the true distance,
 * only "is it within max". Bounded because this runs over the whole universe.
 */
export function withinEdits(a, b, max) {
  if (a === b) return true
  if (Math.abs(a.length - b.length) > max) return false
  const prev = new Array(b.length + 1)
  const cur = new Array(b.length + 1)
  for (let j = 0; j <= b.length; j++) prev[j] = j
  for (let i = 1; i <= a.length; i++) {
    cur[0] = i
    let best = cur[0]
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(
        prev[j] + 1,
        cur[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      )
      if (cur[j] < best) best = cur[j]
    }
    // Whole row already worse than the budget — no completion can recover.
    if (best > max) return false
    for (let j = 0; j <= b.length; j++) prev[j] = cur[j]
  }
  return prev[b.length] <= max
}

/**
 * How much misspelling to forgive, by word length.
 *
 * ⚠️ SHORT WORDS GET NOTHING. At 3 characters an edit distance of 1 makes
 * "cat" match "cap", "car", "bat" — every state code becomes every other state
 * code. A search that quietly matches the wrong state is worse than one that
 * matches nothing, because nobody checks a result that looks plausible.
 */
export function editBudget(token) {
  if (token.length >= 8) return 2
  if (token.length >= 5) return 1
  return 0
}

/* ---- MATCHING ---------------------------------------------------------- */

/* ⚠️ AN ALIAS IS A SET OF WORDS, NOT A PHRASE — and getting this wrong once
 * reintroduced the exact bug this module was written to kill.
 *
 * `llws → "little league world series"` was first tested with a contiguous
 * `hay.includes('little league world series')`. The record is called "Little
 * League BASEBALL World Series", so the phrase is not contiguous and LLWS found
 * NOTHING — the same order-dependence as the original defect, rebuilt inside
 * the fix for it. A multi-word alias satisfies a token when ALL of its words
 * appear, anywhere. */
const variantsOf = (token) => {
  const plain = token.replace(/_/g, ' ')
  const out = [plain]
  for (const v of ALIASES[plain] || []) out.push(v)
  /* A state NAME also matches its CODE, and vice versa. */
  const code = GEO.get(plain)
  if (code) out.push(code)
  return out.map((v) => v.split(/\s+/).filter(Boolean))
}

/**
 * Does the token (or any alias) appear literally? No typo tolerance here.
 *
 * ⚠️ A SHORT VARIANT MUST MATCH A WHOLE WORD. State codes are two letters, and
 * `hay.includes('ca')` is true for Academy, Carolina, Cardinals and half the
 * board. Expanding "california" to a substring test for "ca" would have turned
 * a missing-results bug into a matches-everything bug, which is harder to spot
 * because it looks like the search is working.
 */
export function tokenHitsExact(token, hay, hayWords = null) {
  const words = hayWords || hay.split(/[^a-z0-9]+/).filter(Boolean)
  const typed = token.replace(/_/g, ' ')
  return variantsOf(token).some((parts, i) =>
    parts.every((w) => {
      /* ⚠️ WHAT THE USER TYPED IS A PREFIX; WHAT WE EXPANDED TO IS A CODE.
       *
       * Applying whole-word to everything made a single letter return NOTHING —
       * "b" matched no word exactly, so typing the first character of a query
       * blanked the board and then claimed a data gap. Progressive typing is
       * the normal way this box gets used.
       *
       * So the token the user actually typed keeps substring matching, unless
       * it IS a state code (where "ca" must not match Academy). Everything we
       * expanded to on their behalf — "california" → "ca" — is whole-word,
       * because they never asked for those two letters. */
      const isTyped = i === 0 && parts.length === 1 && w === typed
      const wholeWord = isTyped ? GEO_CODES.has(w) : w.length <= 3
      return wholeWord ? words.includes(w) : hay.includes(w)
    }))
}

/** Typo tolerance — the RESCUE pass only. See searchRecords for why. */
export function tokenHitsFuzzy(token, hay, hayWords) {
  if (tokenHitsExact(token, hay, hayWords)) return true
  const budget = editBudget(token)
  if (!budget) return false
  return hayWords.some((w) => withinEdits(token, w, budget))
}

/** Back-compat name used by callers that just want "does this match at all". */
export const tokenHits = (token, hay, hayWords) => tokenHitsFuzzy(token, hay, hayWords)

/**
 * Collapse multi-word phrases into ONE token before splitting, so
 * "north carolina" survives as a unit and can carry `nc` as its variant.
 * The joined form keeps its words, so a record NAMED "North Carolina ..."
 * still matches on the words themselves.
 *
 * ⚠️ IT FOLDS MULTI-WORD ALIAS KEYS TOO, AND THAT WAS A LATENT BUG.
 * `ALIASES` carried keys like `'high school'`, `'little league'`, `'world
 * series'` and `'track and field'` — every one of them DEAD, because tokenize
 * split the query before the lookup ever ran, so no query could reach a key
 * containing a space. They looked like working vocabulary and were decoration.
 */
const PHRASES = [
  ...GEO_NAMES,
  ...Object.keys(ALIASES),
].filter((k) => k.includes(' ')).sort((a, b) => b.length - a.length)

export function foldGeo(q) {
  let out = ` ${S(q).toLowerCase()} `
  for (const name of PHRASES) {
    out = out.split(` ${name} `).join(` ${name.replace(/ /g, '_')} `)
  }
  return out.trim()
}

export const tokenize = (q) => foldGeo(q).split(/\s+/).filter(Boolean)

/**
 * matchesQuery — every token must hit. Order-independent, field-crossing,
 * alias-aware, typo-tolerant.
 *
 * ⚠️ SUBSTRING PER TOKEN, NOT WHOLE-WORD. Deliberate, and the opposite call to
 * the one huntQuery.js makes about state codes. There the danger was a FALSE
 * POSITIVE — "NC" matching inside "NCHSAA" silently corrupted a search string.
 * Here the cost of strictness is a rep typing "champ" and being told there are
 * no championships, on a list they can already see.
 */
export function matchesQuery(c, query, ownerOf = () => '') {
  const tokens = tokenize(query)
  if (!tokens.length) return true
  const { hay, words } = haystackOf(c, ownerOf)
  return tokens.every((t) => tokenHitsFuzzy(t, hay, words))
}

/* ---- THE ENGINE -------------------------------------------------------- */

/**
 * searchRecords(records, query, ownerOf)
 *   → { mode, results, note, matchedWords, unmatchedWords, tokens }
 *
 * mode:
 *   'all'     no query — everything, no note
 *   'exact'   records matched every word
 *   'partial' NOTHING matched every word, so the closest are returned, ranked,
 *             and the note names the word that killed it
 *   'none'    not one word matched anything — the only honest zero, and it
 *             says which words drew a blank
 *
 * ⚠️ 'partial' IS THE POINT OF THIS FUNCTION. It is the difference between
 * "the board holds no Little League" and "the board holds Little League but
 * not in Ohio", and the old box could not tell those apart — it printed the
 * same 0 for both.
 */
export function searchRecords(records, query, ownerOf = () => '') {
  const list = Array.isArray(records) ? records : []
  const tokens = tokenize(query)
  if (!tokens.length) {
    return { mode: 'all', results: list, note: '', matchedWords: [], unmatchedWords: [], tokens }
  }

  /* Each record's haystack is built ONCE and reused by both passes. */
  const rows = list.map((c) => {
    const { hay, words } = haystackOf(c, ownerOf)
    return { c, hay, words }
  })

  /* ⚠️ FUZZY IS A RESCUE, NOT A PEER — measured, not assumed.
   *
   * With typo tolerance applied on equal footing, "baksetball" matched the
   * BASEBALL records: baksetball → baseball is two deletions, inside the
   * length-10 budget. That is a defensible edit distance and a useless result,
   * and worse, if real basketball records existed it would have quietly mixed
   * baseball in with them. Noise that looks like signal is the failure mode
   * nobody catches, because a search returning plenty looks like it works.
   *
   * So: match literally first. Only if the literal pass finds NOTHING at all
   * does spelling tolerance run. A good exact result is never diluted, and a
   * typo is still rescued — the two goals stop competing. */
  /* ⚠️ SPELLING TOLERANCE IS A PER-WORD RESCUE, NOT A GLOBAL MODE — and both
   * cruder designs were tried and measured first.
   *
   * Applied to every word equally, "baksetball" matched the BASEBALL records
   * (two deletions, inside the length-10 budget): a defensible edit distance
   * and a useless result, which in real data would have quietly mixed baseball
   * in with basketball. Noise that looks like signal is the failure nobody
   * catches, because a search returning plenty looks like it is working.
   *
   * Switched off unless the whole query found nothing, it went the other way:
   * "little leage" returned the right records but reported *nothing on the
   * board says "leage"* — describing a typo as a hole in the data, which is
   * the precise lie this module exists to stop telling.
   *
   * So the rescue is scoped to the WORDS THAT NOBODY USES. A word the board
   * really contains is matched literally and stays sharp; a word that appears
   * nowhere is the one allowed to have been misspelled. */
  /* ONE literal scan, remembered. The exact result per (row, token) is kept so
   * the rescue pass re-tests only the words that need rescuing — without this
   * the whole universe is walked twice on every keystroke. */
  const exactCount = new Map(tokens.map((t) => [t, 0]))
  const exactHits = new Array(rows.length)
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]
    const row = new Array(tokens.length)
    for (let k = 0; k < tokens.length; k++) {
      const hit = tokenHitsExact(tokens[k], r.hay, r.words)
      row[k] = hit
      if (hit) exactCount.set(tokens[k], exactCount.get(tokens[k]) + 1)
    }
    exactHits[i] = row
  }
  const rescue = new Set(tokens.filter((t) => exactCount.get(t) === 0 && editBudget(t) > 0))

  const hitCount = new Map(tokens.map((t) => [t, 0]))
  const scored = []
  let corrected = false
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]
    let hits = 0
    for (let k = 0; k < tokens.length; k++) {
      const t = tokens[k]
      /* A rescue word had ZERO literal hits anywhere by definition, so the
       * cached false is authoritative and only the fuzzy test is worth running. */
      const hit = rescue.has(t) ? tokenHitsFuzzy(t, r.hay, r.words) : exactHits[i][k]
      if (hit) {
        hits++
        hitCount.set(t, hitCount.get(t) + 1)
        if (rescue.has(t)) corrected = true
      }
    }
    if (hits > 0) scored.push({ c: r.c, hits })
  }
  const pass = { scored, hitCount, full: scored.filter((s) => s.hits === tokens.length) }

  const matchedWords = tokens.filter((t) => pass.hitCount.get(t) > 0)
  const unmatchedWords = tokens.filter((t) => pass.hitCount.get(t) === 0)

  if (pass.full.length) {
    return {
      mode: corrected ? 'fuzzy' : 'exact',
      results: pass.full.map((s) => s.c),
      /* A corrected spelling MUST announce itself. Silently answering a
       * different question than the one typed is how somebody records a result
       * against the wrong record. */
      note: corrected
        ? `No record spells ${quoteList(tokens.filter((t) => rescue.has(t)))} that way — showing the closest spelling. Check these are what you meant.`
        : '',
      matchedWords, unmatchedWords, tokens,
    }
  }

  if (!pass.scored.length) {
    /* THE ONLY HONEST ZERO — and even this one explains itself, because "we
     * hold nothing called that" is a real finding a rep can act on. */
    return {
      mode: 'none', results: [],
      note: `Nothing on the board matches ${quoteList(tokens)}. That is a gap in the data, not a spelling problem — no record uses ${tokens.length === 1 ? 'that word' : 'any of those words'}.`,
      matchedWords, unmatchedWords, tokens,
    }
  }

  /* NOTHING matched everything, but something matched something. Rank by how
   * many words landed and NAME the word doing the excluding — that word is
   * almost always the answer to "why is this empty". */
  const best = Math.max(...pass.scored.map((s) => s.hits))
  return {
    mode: 'partial',
    results: pass.scored.filter((s) => s.hits === best).map((s) => s.c),
    note: unmatchedWords.length
      ? `No record matches all ${tokens.length} words — nothing on the board says ${quoteList(unmatchedWords)}. Showing what matches the rest.`
      : `No record matches all ${tokens.length} words together. Showing the closest — these match ${best} of ${tokens.length}.`,
    matchedWords, unmatchedWords, tokens,
  }
}

/* ⚠️ UNFOLD BEFORE SHOWING. Phrases are joined with an underscore so they
 * survive tokenising — an internal mechanic the user never typed and must never
 * read. Without this the honest-zero note said: nothing on the board matches
 * “little_league”. */
const quoteList = (xs) => {
  const q = xs.map((x) => `“${String(x).replace(/_/g, ' ')}”`)
  if (q.length <= 1) return q.join('')
  if (q.length === 2) return `${q[0]} or ${q[1]}`
  return `${q.slice(0, -1).join(', ')} or ${q[q.length - 1]}`
}
